import { useState, useEffect } from "react";
import { Switch } from "@shadcn-in-nx/ui/switch";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { Bluelagoon } from "@bluelagoon";
import { toast } from "sonner";

export function AutoReloadSettingsModal({
    isOpen,
    onClose,
    platform,
    platformName,
    paymentMethod,
    onSuccess,
    t,
}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [autoReloadEnabled, setAutoReloadEnabled] = useState(true);
    const [threshold, setThreshold] = useState("25");
    const [reloadAmount, setReloadAmount] = useState("50");

    useEffect(() => {
        if (isOpen) {
            loadSettings();
        }
    }, [isOpen, platform]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await Bluelagoon.getAutoChargeSettings({
                platform,
            });

            if (response?.data?.data) {
                const settings = response.data.data;
                setAutoReloadEnabled(settings.autoChargeEnabled);
                setThreshold(settings.autoChargeThreshold?.toString() || "5");
                setReloadAmount(settings.autoChargeAmount?.toString() || "20");
            }
        } catch (error) {
            console.error("Error loading settings:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        try {
            setSaving(true);

            await Bluelagoon.updateAutoChargeSettings({
                platform,
                autoChargeEnabled: autoReloadEnabled,
                autoChargeThreshold: parseFloat(threshold),
                autoChargeAmount: parseFloat(reloadAmount),
            });

            if (onSuccess) {
                await onSuccess();
            }
            onClose();
        } catch (error) {
            console.error("Error updating settings:", error);
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-xl w-full"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-bold text-gray-900">
                        {t("billing.updateAutoReloadSettings") ||
                            "Update auto reload settings"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Auto Reload Toggle */}
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="font-medium text-gray-900 mb-1">
                                {t("billing.autoReloadCredits") ||
                                    "Auto reload credits"}
                            </div>
                            <p className="text-sm text-gray-600">
                                {t("billing.autoReloadDescription") ||
                                    "Avoid service disruptions by auto-reloading credits when your balance reaches a specified minimum"}
                            </p>
                        </div>
                        <Switch
                            checked={autoReloadEnabled}
                            onCheckedChange={setAutoReloadEnabled}
                            className="ml-4"
                        />
                    </div>

                    {/* Settings Inputs */}
                    {autoReloadEnabled && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("billing.whenBalanceReaches") ||
                                        "When credit balance reaches:"}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        value={threshold}
                                        onChange={(e) =>
                                            setThreshold(e.target.value)
                                        }
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                        placeholder={"5"}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("billing.bringBalanceBackTo") ||
                                        "Bring credit balance back up to:"}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        value={reloadAmount}
                                        onChange={(e) =>
                                            setReloadAmount(e.target.value)
                                        }
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="1"
                                        placeholder="20"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Method Info */}
                    {paymentMethod && autoReloadEnabled && (
                        <p className="text-sm text-gray-600">
                            {t("billing.creditsWillBeCharged") ||
                                "Credits will be charged to"}{" "}
                            <span className="font-medium capitalize">
                                {paymentMethod.brand}
                            </span>{" "}
                            {t("billing.endingIn") || "ending in"}{" "}
                            {paymentMethod.last4}
                        </p>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-3 justify-end pt-4">
                        <UiButton
                            onClick={onClose}
                            variant="outline"
                            disabled={saving}
                            className="px-6"
                        >
                            {t("common.cancel") || "Cancel"}
                        </UiButton>
                        <UiButton
                            onClick={handleUpdate}
                            disabled={saving}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-6"
                        >
                            {saving
                                ? t("billing.autoCharge.saving") || "Saving..."
                                : t("billing.update") || "Update"}
                        </UiButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
